import type { Entry } from "@/shared/types/entries";
import { roundUnitsForMatter } from "@/shared/utils/zurich";

export type ReconcileAction = "merge" | "group" | "collapse";

/**
 * Return the deduped non-empty values for a given field across source entries,
 * in first-appearance order. Empty strings, null, and undefined are treated as
 * "no value" and don't appear in the result.
 */
export function distinctValues<K extends keyof Entry>(
	entries: Entry[],
	field: K,
): NonNullable<Entry[K]>[] {
	const seen = new Set<unknown>();
	const result: NonNullable<Entry[K]>[] = [];
	for (const entry of entries) {
		const value = entry[field];
		if (value === null || value === undefined || value === "") continue;
		if (seen.has(value)) continue;
		seen.add(value);
		result.push(value as NonNullable<Entry[K]>);
	}
	return result;
}

/** True if at least one source has the field empty AND at least one has a value. */
export function hasMixedEmptyAndFilled<K extends keyof Entry>(
	entries: Entry[],
	field: K,
): boolean {
	let hasEmpty = false;
	let hasFilled = false;
	for (const entry of entries) {
		const value = entry[field];
		if (value === null || value === undefined || value === "") hasEmpty = true;
		else hasFilled = true;
	}
	return hasEmpty && hasFilled;
}

/** Matter-aware billing candidate for the units chip row. */
export interface UnitsCandidate {
	/** Raw second total for this aggregation strategy (un-snapped). */
	rawSeconds: number;
	/** Matter-aware snapped units value — what would be billed. */
	units: number;
	/**
	 * Snapped seconds = `round(units * 3600)`. This is the value pushed into
	 * the form's `unitsSeconds` state on chip pick, and what the backend will
	 * re-derive `units` from via its own `calculateUnits`. Sending the snapped
	 * value (not the raw) keeps the dialog preview and the persisted entry in
	 * lock-step even if the backend's snap rule ever drifts.
	 */
	snappedSeconds: number;
}

/**
 * Two units candidates for the chip row, matter-aware:
 *
 *  - `sumUnits`: sum each source's pre-rounded billing units, then snap.
 *    Matches the backend's `sumUnitsPerEntry` strategy.
 *  - `sumSeconds`: sum each source's raw `units_seconds`, then snap once.
 *    Matches the backend's `sumUnitsSeconds` strategy (and `calculateUnits`).
 *
 * The two candidates agree when each source's raw seconds already happen to
 * fall on the grid; they diverge when sources were logged off-grid (e.g.
 * stopwatch entries < 6 min that each rounded up to 0.1, so summing units
 * over-bills compared to summing the raw elapsed time).
 *
 * The `client` argument drives the Zurich rule (`isZurichMatter` ⇒ 0.05 floor
 * for ≤180s; otherwise standard 0.1 grid). Pass the *current* selected matter's
 * client so the candidates re-snap when the user switches matters mid-dialog.
 */
export function unitsCandidates(
	entries: Entry[],
	client: string | undefined | null,
): { sumUnits: UnitsCandidate; sumSeconds: UnitsCandidate } {
	const sumUnitsRaw = entries.reduce((acc, e) => acc + (e.duration ?? 0), 0);
	const sumUnitsRawSeconds = Math.round(sumUnitsRaw * 3600);
	const sumSecondsRawSeconds = entries.reduce((acc, e) => {
		const raw = e.units_seconds ?? Math.round((e.duration ?? 0) * 3600);
		return acc + raw;
	}, 0);

	const sumUnitsSnapped = roundUnitsForMatter(sumUnitsRawSeconds, client);
	const sumSecondsSnapped = roundUnitsForMatter(sumSecondsRawSeconds, client);

	return {
		sumUnits: {
			rawSeconds: sumUnitsRawSeconds,
			units: sumUnitsSnapped,
			snappedSeconds: Math.round(sumUnitsSnapped * 3600),
		},
		sumSeconds: {
			rawSeconds: sumSecondsRawSeconds,
			units: sumSecondsSnapped,
			snappedSeconds: Math.round(sumSecondsSnapped * 3600),
		},
	};
}

/**
 * Predicate the toolbar uses to decide between auto-submit and opening the
 * dialog. Returns true when at least one inheritable field disagrees across
 * sources (or, for merge, the two billable-units candidates diverge).
 *
 * `client` is needed for the units divergence check — Zurich's 0.05 floor can
 * change whether two candidates collapse to the same billable value.
 */
export function hasConflicts(
	entries: Entry[],
	action: ReconcileAction,
	client: string | undefined | null,
): boolean {
	if (entries.length < 2) return false;
	if (distinctValues(entries, "date").length > 1) return true;
	if (distinctValues(entries, "matter_id").length > 1) return true;
	if (distinctValues(entries, "task").length > 1) return true;
	if (distinctValues(entries, "activity").length > 1) return true;
	if (action === "merge") {
		const { sumUnits, sumSeconds } = unitsCandidates(entries, client);
		if (sumUnits.units !== sumSeconds.units) return true;
	}
	return false;
}

/** Earliest non-empty start_time across sources (HH:MM display). */
export function minStartTime(entries: Entry[]): string | null {
	let best: string | null = null;
	for (const e of entries) {
		if (!e.start_time) continue;
		if (best === null || e.start_time < best) best = e.start_time;
	}
	return best;
}

/** Latest non-empty end_time across sources (HH:MM display). */
export function maxEndTime(entries: Entry[]): string | null {
	let best: string | null = null;
	for (const e of entries) {
		if (!e.end_time) continue;
		if (best === null || e.end_time > best) best = e.end_time;
	}
	return best;
}
